<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import Notification from "@/utils/plugins/NotificationService";
import ApiApplicationService from "@/api/services/apiApplication.service";
import EmployeePermissionService from "@/api/services/employeePermission.service";
import ResourceService from "@/api/services/resource.service";

const route = useRoute();
const router = useRouter();
const applicationId = route.params.id;

const activeTab = ref("permissions");
const saving = ref(false);
const application = ref(null);
const departments = ref([]);
const moduleLabels = ref({});
const actionLabels = ref({});
const rows = ref([]);
const initialRows = ref([]);

const scopeTypeOptions = [
  { value: "global", label: "Global" },
  { value: "client", label: "Cliente" },
  { value: "department", label: "Departamento" },
  { value: "employee_team", label: "Equipo de empleado" },
  { value: "project", label: "Proyecto" },
];

const initialsOf = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const application_ = computed(() => ({
  initials: initialsOf(application.value?.name),
  name: application.value?.name ?? "",
  description: application.value?.description ?? "",
  prefix: application.value?.prefix ?? "",
  active: application.value ? application.value.active && application.value.currentlyValid : false,
  createdAt: application.value?.createdAt ? dayjs(application.value.createdAt).format("DD/MM/YYYY HH:mm") : "—",
  expiresAt: application.value?.expiresAt ? dayjs(application.value.expiresAt).format("DD/MM/YYYY HH:mm") : "Sin caducidad",
  lastUsedAt: application.value?.lastUsedAt ? dayjs(application.value.lastUsedAt).format("DD/MM/YYYY HH:mm") : "Nunca",
  revokedAt: application.value?.revokedAt ? dayjs(application.value.revokedAt).format("DD/MM/YYYY HH:mm") : null,
}));

const rowKey = (row) => `${row.module}:${row.action}`;

const isDirty = computed(() => rows.value.some((row, index) => {
  const initial = initialRows.value[index];
  return row.granted !== initial.granted || row.scopeType !== initial.scopeType || row.scopeId !== initial.scopeId;
}));

const buildRows = (matrix) => matrix.map((row) => ({
  module: row.module,
  action: row.action,
  granted: !!row.granted,
  scopeType: row.scopeType ?? "global",
  scopeId: row.scopeId ?? null,
}));

const loadApplication = () => {
  ApiApplicationService.getApiApplication(applicationId).then((res) => {
    if (!res) {
      router.push({ name: "security-api-applications" });
      return;
    }
    application.value = res.data;
  });
};

const loadCatalog = () => {
  EmployeePermissionService.getCatalogActions().then((res) => {
    if (!res) return;
    (res.data ?? []).forEach((mod) => {
      moduleLabels.value[mod.module] = mod.label;
      mod.actions.forEach((action) => {
        actionLabels.value[`${mod.module}:${action.value}`] = action.label;
      });
    });
  });
};

const loadDepartments = () => {
  ResourceService.getDepartments().then((res) => {
    if (!res) return;
    departments.value = (res.data["hydra:member"] ?? []).map((d) => ({ id: d.id, name: d.name }));
  });
};

const loadPermissions = () => {
  ApiApplicationService.getPermissions(applicationId).then((res) => {
    if (!res) return;
    rows.value = buildRows(res.data ?? []);
    initialRows.value = rows.value.map((row) => ({ ...row }));
  });
};

const moduleLabel = (moduleValue) => moduleLabels.value[moduleValue] ?? moduleValue;
const actionLabel = (row) => actionLabels.value[`${row.module}:${row.action}`] ?? row.action;

const moduleSpanFor = (index) => {
  if (index > 0 && rows.value[index - 1].module === rows.value[index].module) return 0;
  let span = 1;
  while (rows.value[index + span] && rows.value[index + span].module === rows.value[index].module) span += 1;
  return span;
};

const onScopeTypeChange = (row) => {
  if (row.scopeType === "global") row.scopeId = null;
};

const save = () => {
  if (!isDirty.value) {
    Notification.info("No hay cambios que guardar.", 3000);
    return;
  }

  const changes = rows.value
    .filter((row, index) => {
      const initial = initialRows.value[index];
      return row.granted !== initial.granted || row.scopeType !== initial.scopeType || row.scopeId !== initial.scopeId;
    })
    .map((row) => ({
      module: row.module,
      action: row.action,
      granted: row.granted,
      scopeType: row.scopeType,
      scopeId: row.scopeType === "global" ? null : row.scopeId,
    }));

  saving.value = true;
  ApiApplicationService.updatePermissions(applicationId, changes)
    .then((res) => {
      if (!res) return;
      initialRows.value = rows.value.map((row) => ({ ...row }));
      Notification.success("Permisos y alcance guardados correctamente.", 3500);
    })
    .finally(() => { saving.value = false; });
};

const pending = (label) =>
  Notification.info(`${label} estará disponible próximamente.`, 3500);

onMounted(() => {
  loadApplication();
  loadCatalog();
  loadDepartments();
  loadPermissions();
});
</script>

<template>
  <section class="api-application-permissions">
    <HeaderTitle title="Seguridad" />

    <SecuritySectionTitle title="Aplicación API · Permisos y alcance" />

    <el-card class="api-application-summary">
      <div class="api-application-summary__identity">
        <span class="api-application-summary__avatar">{{ application_.initials }}</span>
        <span><b>{{ application_.name }}</b><small>{{ application_.description }}</small></span>
      </div>
      <dl>
        <div>
          <dt>Prefijo de clave</dt>
          <dd>{{ application_.prefix }}</dd>
        </div>
        <div>
          <dt>Estado</dt>
          <dd>
            <span class="status" :class="application_.active ? 'active' : 'inactive'">{{ application_.revokedAt ? 'Revocada' : (application_.active ? 'Activa' : 'Inactiva') }}</span>
          </dd>
        </div>
        <div>
          <dt>Creada</dt>
          <dd>{{ application_.createdAt }}</dd>
        </div>
        <div>
          <dt>Caduca</dt>
          <dd>{{ application_.expiresAt }}</dd>
        </div>
        <div>
          <dt>Último uso</dt>
          <dd>{{ application_.lastUsedAt }}</dd>
        </div>
      </dl>
    </el-card>

    <el-card class="api-permissions-card">
      <el-tabs v-model="activeTab" class="security-tabs">
        <el-tab-pane label="PERMISOS Y ALCANCE" name="permissions" />
        <el-tab-pane label="DETALLES" name="details" />
        <el-tab-pane label="CLAVES" name="keys" />
        <el-tab-pane label="HISTORIAL" name="history" />
      </el-tabs>

      <template v-if="activeTab === 'permissions'">
        <p class="security-notice">
          <font-awesome-icon icon="exclamation-circle" /> Esta aplicación tiene
          permisos sensibles. Revisa cuidadosamente cada permiso y su alcance.
        </p>

        <div class="table-not-mobile api-permissions-table">
          <table>
            <thead>
              <tr>
                <th>Módulo</th>
                <th class="api-permissions-table__action">Acción</th>
                <th class="api-permissions-table__granted">Concedido</th>
                <th class="api-permissions-table__scope-type">Tipo de alcance</th>
                <th class="api-permissions-table__scope-id">ID de alcance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="rowKey(row)">
                <td v-if="moduleSpanFor(index)" :rowspan="moduleSpanFor(index)" class="api-permissions-table__module">
                  {{ moduleLabel(row.module) }}
                </td>
                <td class="api-permissions-table__action">{{ actionLabel(row) }}</td>
                <td class="api-permissions-table__granted">
                  <input type="checkbox" v-model="row.granted" :aria-label="`Conceder ${actionLabel(row)}`" />
                </td>
                <td class="api-permissions-table__scope-type">
                  <el-select v-model="row.scopeType" class="w-100" :disabled="!row.granted"
                    @change="onScopeTypeChange(row)">
                    <el-option v-for="opt in scopeTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
                  </el-select>
                </td>
                <td class="api-permissions-table__scope-id">
                  <el-select v-if="row.scopeType === 'department'" v-model="row.scopeId" class="w-100"
                    :disabled="!row.granted" placeholder="Departamento">
                    <el-option v-for="dep in departments" :key="dep.id" :label="dep.name" :value="dep.id" />
                  </el-select>
                  <el-input v-else-if="row.scopeType !== 'global'" v-model.number="row.scopeId" type="number"
                    :disabled="!row.granted" placeholder="ID" />
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-mobile api-permissions-mobile">
          <el-card v-for="row in rows" :key="rowKey(row)">
            <p><b>Módulo:</b>{{ moduleLabel(row.module) }}</p>
            <p><b>Acción:</b>{{ actionLabel(row) }}</p>
            <div class="form-item">
              <label>Concedido</label>
              <input type="checkbox" v-model="row.granted" />
            </div>
            <div class="form-item">
              <label>Tipo de alcance</label>
              <el-select v-model="row.scopeType" class="w-100" :disabled="!row.granted" @change="onScopeTypeChange(row)">
                <el-option v-for="opt in scopeTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
            </div>
            <div class="form-item">
              <label>ID de alcance</label>
              <el-select v-if="row.scopeType === 'department'" v-model="row.scopeId" class="w-100"
                :disabled="!row.granted" placeholder="Departamento">
                <el-option v-for="dep in departments" :key="dep.id" :label="dep.name" :value="dep.id" />
              </el-select>
              <el-input v-else-if="row.scopeType !== 'global'" v-model.number="row.scopeId" type="number"
                :disabled="!row.granted" placeholder="ID" />
              <span v-else>—</span>
            </div>
          </el-card>
        </div>
      </template>

      <p v-else class="security-empty-tab" @click="pending(activeTab)">
        Esta sección estará disponible próximamente.
      </p>
    </el-card>

    <template v-if="activeTab === 'permissions'">
      <div class="api-permissions-footer">
        <div class="api-permissions-actions">
          <button type="button" class="btn btn_red" :disabled="saving" @click="save">
            GUARDAR CAMBIOS
          </button>
        </div>
      </div>
    </template>
  </section>
</template>

<style lang="scss" scoped>
.api-application-permissions {
  color: #30385d;
}

.security-subtitle {
  align-items: center;
  display: flex;
  margin-bottom: 12px;
}

.security-subtitle .svg-inline--fa {
  color: #474ce6;
  font-size: 27px;
}

.security-subtitle .title-text {
  color: #474ce6;
  font-size: 2.5em;
  margin-left: 1rem;
}

.api-application-summary {
  margin-bottom: 18px;
}

.api-application-summary :deep(.el-card__body) {
  align-items: center;
  display: flex;
  gap: 28px;
  padding: 18px 24px;
}

.api-application-summary__identity {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 210px;
}

.api-application-summary__identity>span:last-child {
  display: grid;
  gap: 4px;
}

.api-application-summary__identity b {
  font-size: 1.08rem;
}

.api-application-summary__identity small {
  color: #7c83a2;
  display: block;
  font-size: 0.8rem;
  font-weight: 400;
}

.api-application-summary__avatar {
  align-items: center;
  background: #6a55df;
  border: 3px solid #d8dcff;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  flex: 0 0 58px;
  font-size: 0.85rem;
  font-weight: 800;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.api-application-summary dl {
  display: grid;
  flex: 1;
  gap: 14px 26px;
  grid-template-columns: repeat(5, minmax(82px, 1fr));
  margin: 0;
}

.api-application-summary dt {
  color: #7c83a2;
  font-size: 0.8rem;
  font-weight: 800;
}

.api-application-summary dd {
  font-size: 0.92rem;
  font-weight: 800;
  margin: 5px 0 0;
}

.status {
  border: 1px solid;
  border-radius: 15px;
  font-size: 0.72rem;
  padding: 3px 10px;
}

.active {
  background: #effbf4;
  border-color: #c9eed9;
  color: #279653;
}

.inactive {
  background: #fff7ef;
  border-color: #ffe0ba;
  color: #fb6b21;
}

.api-permissions-card {
  margin-bottom: 14px;
}

.security-tabs :deep(.el-tabs__item) {
  color: #626a91;
  font-size: 0.72rem;
  font-weight: 800;
}

.security-tabs :deep(.el-tabs__item.is-active) {
  color: #474ce6;
}

.security-notice {
  background: #fff5d8;
  border: 1px solid #f7d20d;
  border-radius: 8px;
  color: #715e22;
  font-size: 0.78rem;
  margin: 4px 0 14px;
  padding: 8px 10px;
}

.security-notice .svg-inline--fa {
  color: #f7c800;
  margin-right: 5px;
}

.api-permissions-table {
  overflow: auto;
}

.api-permissions-table table {
  border-collapse: collapse;
  min-width: 820px;
  width: 100%;
}

.api-permissions-table th {
  color: #474ce6;
  padding: 10px 12px;
  text-align: left;
  text-transform: uppercase;
}

.api-permissions-table td {
  border: 1px solid #edf0f7;
  padding: 9px 12px;
  vertical-align: middle;
}

.api-permissions-table__module {
  color: #474ce6;
  font-weight: 800;
}

.api-permissions-table__scope-type,
.api-permissions-table__scope-id {
  min-width: 150px;
}

.api-permissions-table :deep(.el-select__wrapper) {
  min-height: 31px;
}

.api-permissions-table__granted {
  text-align: center;
}

.table-mobile {
  display: none;
}

.api-permissions-footer {
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: flex-end;
  margin-bottom: 22px;
}

.api-permissions-actions {
  display: flex;
  flex: 0 0 auto;
}

.api-permissions-actions .btn {
  color: #fff !important;
  min-height: 38px;
  padding-left: 20px;
  padding-right: 20px;
  width: auto;
}

.security-empty-tab {
  color: #7c83a2;
  cursor: pointer;
  text-align: center;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (min-width: 1200px) and (max-width: 1439px) {
  .api-application-summary :deep(.el-card__body) {
    align-items: flex-start;
  }

  .api-application-summary dl {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 768px) and (max-width: 1199px) {
  .api-application-summary :deep(.el-card__body) {
    align-items: flex-start;
  }

  .api-application-summary dl {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: grid;
    gap: 12px;
  }

  .security-subtitle .title-text {
    font-size: 2em;
  }

  .api-application-summary :deep(.el-card__body) {
    align-items: flex-start;
    flex-direction: column;
    padding: 16px;
  }

  .api-application-summary dl {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .api-application-summary dl>div {
    align-items: center;
    display: grid;
    gap: 10px;
    grid-template-columns: 105px minmax(0, 1fr);
  }

  .api-application-summary dd {
    margin: 0;
  }

  .security-tabs :deep(.el-tabs__header) {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .security-tabs :deep(.el-tabs__nav) {
    white-space: nowrap;
  }

  .api-permissions-mobile :deep(.el-card__body) {
    padding: 14px;
  }

  .api-permissions-mobile p {
    display: grid;
    gap: 7px;
    grid-template-columns: 125px minmax(0, 1fr);
    margin: 9px 0 0;
    word-break: break-word;
  }

  .api-permissions-mobile p b,
  .api-permissions-mobile label {
    color: #474ce6;
  }

  .api-permissions-mobile .form-item {
    margin-top: 12px;
  }

  .api-permissions-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .api-permissions-actions .btn {
    width: 100%;
  }
}
</style>
